'use client';

import { ReactNode } from 'react';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import AdminSidebar from '@/components/admin/AdminSidebar';
import { SupportChatWidget } from '@/app/admin/_components/SupportChatWidget';
import { Toaster } from '@/components/ui/toaster';
import AuthGuard from '@/components/admin/auth/AuthGuard';
import { RBACProvider } from '@/lib/auth/rbac';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { FeatureAccessProvider } from '@/hooks/useFeatureAccess';
import { ActiveTenantProvider, useActiveTenant } from '@/hooks/useActiveTenant';
import { AdminViewAsDropdown } from '@/components/admin/AdminViewAsDropdown';
import { TrialExpiredBanner } from '@/components/admin/TrialExpiredBanner';
import { SentryUserProvider } from '@/components/providers/SentryUserProvider';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { CommandPalette } from './_components/CommandPalette';
import { TenantSubscriptionProvider } from '@/components/subscription/TenantSubscriptionContext';
import { TrialAwarenessBanner } from './_components/TrialAwarenessBanner';
import { NotificationBell } from './_components/NotificationBell';
import { NotificationsProvider } from './_components/NotificationsProvider';
import { PWARegistrar } from './_components/PWARegistrar';
import { StepUpProvider } from '@/components/auth/StepUpProvider';
import { OnboardingGate } from './_components/OnboardingGate';
import '@/styles/mobile-admin.css';

const PUBLIC_ROUTES = [
  '/admin/login',
  '/admin/register',
  '/admin/forgot-password',
  '/admin/reset-password',
  '/admin/setup-account',
];

export default function AdminLayout({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const isPublicRoute = PUBLIC_ROUTES.includes(pathname);
  // /admin/welcome runs inside the authenticated shell (AuthGuard +
  // ActiveTenantProvider) but bypasses the sidebar + topbar chrome so
  // the post-signup wizard reads as a focused full-screen flow rather
  // than a dashboard-with-a-modal.
  const isWelcomeWizard = pathname === '/admin/welcome';

  // Public auth pages: minimal shell with just language switcher and centered content.
  if (isPublicRoute) {
    return (
      <ErrorBoundary>
        <div className="relative min-h-screen bg-gray-50">
          <div className="absolute right-4 top-4 z-10">
            <LanguageSwitcher />
          </div>
          <main className="flex-1">{children}</main>
          <Toaster />
        </div>
      </ErrorBoundary>
    );
  }

  // Welcome-wizard shell: authenticated but chrome-free. Reuses the
  // same providers so the wizard's API calls (status, complete-setup)
  // see the right session + tenant context.
  if (isWelcomeWizard) {
    return (
      <SentryUserProvider>
        <AuthGuard>
          {/* ActiveTenantProvider has to wrap RBACProvider now — RBAC is a
              shim that consumes the unified tenant + permissions context. */}
          <ActiveTenantProvider>
            <RBACProvider>
              <ErrorBoundary>
                <main>{children}</main>
                <Toaster />
              </ErrorBoundary>
            </RBACProvider>
          </ActiveTenantProvider>
        </AuthGuard>
      </SentryUserProvider>
    );
  }

  // Authenticated admin shell: sidebar + topbar + content
  return (
    <SentryUserProvider>
      <AuthGuard>
        {/* Provider-Tree Reorder (F-500): ActiveTenantProvider is now
            the single source of truth for tenant + role + permissions.
            RBACProvider is a thin shim that exposes the same data
            through the legacy `useRBAC()` API. */}
        <ActiveTenantProvider>
         <RBACProvider>
          <FeatureAccessProviderWrapper>
            <TenantSubscriptionProvider>
            <ErrorBoundary>
              <StepUpProvider>
              <NotificationsProvider>
              <OnboardingGate />
              <PWARegistrar />
              <div className="flex min-h-screen bg-gray-50">
                {/* Desktop sidebar (AdminSidebar handles its own sticky/width/border) */}
                <AdminSidebar />

                <div className="flex min-w-0 flex-1 flex-col">
                  {/* Mobile topbar — sticky, blur, hamburger triggers the sidebar Sheet */}
                  <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-gray-200/80 bg-white/85 px-4 backdrop-blur supports-[backdrop-filter]:bg-white/70 md:hidden">
                    <AdminSidebar isMobile />
                    <Image
                      src="/logo.svg"
                      alt="EazyBooking"
                      width={120}
                      height={28}
                      className="h-7 w-auto"
                    />
                    <div className="ml-auto">
                      <NotificationBell />
                    </div>
                  </header>

                  {/* Desktop topbar — Benachrichtigungs-Glocke rechts. */}
                  <div className="hidden items-center justify-end border-b border-gray-200/80 bg-white px-6 py-1.5 md:flex">
                    <NotificationBell />
                  </div>

                  {/* Desktop admin "view-as" dropdown for super admins */}
                  <AdminViewAsDropdown />

                  {/* Trial-awareness banner — global, sits above page content
                      for any admin route, dismissable per session. */}
                  <TrialAwarenessBanner />

                  {/* Content */}
                  <main className="flex-1 overflow-auto">{children}</main>
                </div>

                <Toaster />
                <CommandPalette />
                <SupportChatWidget />
              </div>
              </NotificationsProvider>
              </StepUpProvider>
            </ErrorBoundary>
            </TenantSubscriptionProvider>
          </FeatureAccessProviderWrapper>
         </RBACProvider>
        </ActiveTenantProvider>
      </AuthGuard>
    </SentryUserProvider>
  );
}

/**
 * Bridge `useActiveTenant()` → `FeatureAccessProvider`.
 *
 * The canonical tenantId comes from the server via ActiveTenantProvider
 * (mounted one level up). No cookie reads, no localStorage reads — those
 * happen exactly once inside the resolver, where the server has final say.
 * If anyone ever re-introduces direct cookie/localStorage reads at this
 * layer, they're recreating the bug class this refactor exists to kill.
 */
function FeatureAccessProviderWrapper({ children }: { children: ReactNode }) {
  const { tenantId } = useActiveTenant();

  return (
    <FeatureAccessProvider tenantId={tenantId}>
      <TrialExpiredBanner />
      {children}
    </FeatureAccessProvider>
  );
}
